import React, { useState, useEffect, useCallback } from 'react';
import { auth, googleProvider } from '../../firebase';
import { signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import { verifyAdminStatus, saveAdminItem, deleteAdminItem } from '../../services/data';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const [user, setUser] = useState(null);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [loading, setLoading] = useState(true);
    
    // UI State
    const [activeTab, setActiveTab] = useState('projects');
    const [items, setItems] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [msg, setMsg] = useState({ text: '', type: '' });

    // Dynamic Form State
    const [formData, setFormData] = useState({});

    const TABS = [
        { id: 'projects', label: 'Projects', icon: '📁' },
        { id: 'products', label: 'Products', icon: '📦' },
        { id: 'about', label: 'About Me', icon: '👤' },
        { id: 'worked_with', label: 'Companies', icon: '🏢' },
        { id: 'socialMedia', label: 'Social Links', icon: '🔗' }
    ];

    const fetchData = useCallback(async () => {
        try {
            const token = await auth.currentUser.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}admin/all-data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            
            if (['projects', 'products', 'worked_with'].includes(activeTab)) {
                const listData = data[activeTab] ? Object.entries(data[activeTab]).map(([id, val]) => ({...val, id})) : [];
                setItems(listData.reverse());
            } else if (activeTab === 'about') {
                setFormData(data.about || {});
            } else if (activeTab === 'socialMedia') {
                setFormData(data.about?.socialMedia || {});
            }
        } catch (error) {
            setMsg({ text: 'Failed to fetch database content', type: 'error' });
            console.error("Fetch error", error);
        }
    }, [activeTab]);

    useEffect(() => {
        setFormData({});
        setIsEditing(false);
        setMsg({ text: '', type: '' });
    }, [activeTab]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                const token = await currentUser.getIdToken();
                const verified = await verifyAdminStatus(token);
                setIsAuthorized(verified);
                if (verified) fetchData();
            }
            setUser(currentUser);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [fetchData]);

    const handleSave = async (e) => {
        e.preventDefault();
        setMsg({ text: 'Saving changes...', type: 'info' });
        
        try {
            // Pick only relevant fields to prevent "data bleeding" from other tabs
            let sanitizedData = {};
            if (activeTab === 'projects') {
                sanitizedData = { 
                    title: formData.title, 
                    description: formData.description, 
                    image: formData.image, 
                    techStacks: formData.techStacks, 
                    isPrivate: formData.isPrivate 
                };
            } else if (activeTab === 'products') {
                sanitizedData = { 
                    title: formData.title, 
                    description: formData.description, 
                    image: formData.image, 
                    techStacks: formData.techStacks, 
                    isPrivate: formData.isPrivate,
                    link: formData.link 
                };
            } else if (activeTab === 'worked_with') {
                sanitizedData = { Name: formData.Name, logo: formData.logo };
            } else if (activeTab === 'about') {
                sanitizedData = { heading: formData.heading, paragraph: formData.paragraph, profileImg: formData.profileImg };
            } else if (activeTab === 'socialMedia') {
                sanitizedData = { ...formData }; // Social media is a direct map
            }

            // Keep the ID if we are editing an existing item
            if (formData.id) sanitizedData.id = formData.id;

            const token = await auth.currentUser.getIdToken();
            await saveAdminItem(activeTab, sanitizedData, token);
            setMsg({ text: 'Awesome! Changes saved successfully.', type: 'success' });
            
            if (!['about', 'socialMedia'].includes(activeTab)) {
                setIsEditing(false);
                setFormData({});
                fetchData();
            }
        } catch (error) {
            setMsg({ text: 'Oops! Failed to save changes.', type: 'error' });
            console.error(error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to remove this item?')) return;
        setMsg({ text: 'Removing...', type: 'info' });
        try {
            const token = await auth.currentUser.getIdToken();
            await deleteAdminItem(activeTab, id, token);
            setMsg({ text: 'Item removed successfully.', type: 'success' });
            fetchData();
        } catch (error) {
            setMsg({ text: 'Failed to delete item.', type: 'error' });
        }
    };

    const startEdit = (item) => {
        setFormData(item);
        setIsEditing(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleLogin = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            const token = await result.user.getIdToken();
            const verified = await verifyAdminStatus(token);
            setIsAuthorized(verified);
        } catch (error) {
            setMsg({ text: 'Authentication failed', type: 'error' });
        }
    };

    if (loading) return <div className="admin-loading"><span>⚡</span> Checking Secure Path...</div>;

    if (!user || !isAuthorized) {
        return (
            <div className="admin-login-overlay">
                <div className="login-card">
                    <div className="login-icon">🔒</div>
                    <h2>Admin Access</h2>
                    {!user ? (
                        <>
                            <p>Enter the hub by authenticating with your master account.</p>
                            <button onClick={handleLogin} className="google-login-btn">Sign in with Google</button>
                        </>
                    ) : (
                        <div className="error">
                            <p>Unauthorized: {user.email} is not the master.</p>
                            <button onClick={() => signOut(auth)} className="google-login-btn">Sign Out</button>
                        </div>
                    )}
                    <button onClick={() => window.location.href = '/'} className="back-btn">Exit to Portfolio</button>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard-layout">
            <aside className="admin-sidebar">
                <div className="sidebar-header">
                    <div className="logo">KKS<span>HUB</span></div>
                </div>
                <nav className="sidebar-nav">
                    {TABS.map(tab => (
                        <button 
                            key={tab.id} 
                            className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <span className="tab-icon">{tab.icon}</span>
                            <span className="tab-label">{tab.label}</span>
                        </button>
                    ))}
                </nav>
                <div className="sidebar-footer">
                    <div className="user-info">
                        <img src={user.photoURL} alt="Admin" />
                        <span>Kshitij</span>
                    </div>
                    <button onClick={() => signOut(auth)} className="sidebar-logout">Logout</button>
                </div>
            </aside>

            <main className="admin-main">
                {msg.text && <div className={`admin-toast ${msg.type}`}>{msg.text}</div>}

                <header className="main-header">
                    <h1>{TABS.find(t => t.id === activeTab)?.label} Management</h1>
                </header>

                <div className="content-grid">
                    <section className="admin-section form-card">
                        <div className="card-header">
                            <h3>{isEditing ? 'Editing Entry' : `Configure ${activeTab}`}</h3>
                        </div>
                        <form onSubmit={handleSave} className="premium-form">
                            {/* DYNAMIC FIELDS */}
                            {(activeTab === 'projects' || activeTab === 'products') && (
                                <>
                                    <input type="text" placeholder="Title" value={formData.title || ''} onChange={e => setFormData({ ...formData, title: e.target.value })} required />
                                    <textarea placeholder="Detailed Description" value={formData.description || ''} onChange={e => setFormData({ ...formData, description: e.target.value })} required />
                                    <input type="text" placeholder="Image URL / ID" value={formData.image || ''} onChange={e => setFormData({ ...formData, image: e.target.value })} />
                                    <input type="text" placeholder="Tech Stack (comma separated)" value={formData.techStacks || ''} onChange={e => setFormData({ ...formData, techStacks: e.target.value })} />
                                    {activeTab === 'products' && (
                                        <input type="text" placeholder="Product Link (Required for 'Live' status)" value={formData.link || ''} onChange={e => setFormData({ ...formData, link: e.target.value })} />
                                    )}
                                    <label className="checkbox-toggle">
                                        <input type="checkbox" checked={formData.isPrivate === 1} onChange={e => setFormData({ ...formData, isPrivate: e.target.checked ? 1 : 0 })} />
                                        <span>Private Access Only</span>
                                    </label>
                                </>
                            )}

                            {activeTab === 'about' && (
                                <div className="dynamic-form-section">
                                    <label>Main Heading</label>
                                    <input type="text" placeholder="About Heading" value={formData.heading || ''} onChange={e => setFormData({ ...formData, heading: e.target.value })} />
                                    
                                    <div className="paragraph-list-header">
                                        <label>Bio Paragraphs</label>
                                        <button type="button" className="add-para-btn" onClick={() => setFormData({ ...formData, paragraph: [...(formData.paragraph || []), ''] })}>+ Add Paragraph</button>
                                    </div>
                                    
                                    <div className="paragraph-items">
                                        {(formData.paragraph || []).map((para, idx) => (
                                            <div key={idx} className="para-item-wrapper">
                                                <div className="para-ctrls">
                                                    <button type="button" onClick={() => {
                                                        if (idx === 0) return;
                                                        const newArr = [...formData.paragraph];
                                                        [newArr[idx], newArr[idx-1]] = [newArr[idx-1], newArr[idx]];
                                                        setFormData({...formData, paragraph: newArr});
                                                    }}>▲</button>
                                                    <button type="button" onClick={() => {
                                                        if (idx === formData.paragraph.length - 1) return;
                                                        const newArr = [...formData.paragraph];
                                                        [newArr[idx], newArr[idx+1]] = [newArr[idx+1], newArr[idx]];
                                                        setFormData({...formData, paragraph: newArr});
                                                    }}>▼</button>
                                                </div>
                                                <textarea 
                                                    placeholder={`Paragraph ${idx + 1}`} 
                                                    value={para} 
                                                    onChange={e => {
                                                        const newArr = [...formData.paragraph];
                                                        newArr[idx] = e.target.value;
                                                        setFormData({ ...formData, paragraph: newArr });
                                                    }} 
                                                />
                                                <button type="button" className="para-del-btn" onClick={() => {
                                                    const newArr = formData.paragraph.filter((_, i) => i !== idx);
                                                    setFormData({ ...formData, paragraph: newArr });
                                                }}>×</button>
                                            </div>
                                        ))}
                                    </div>
                                    
                                    <label>Profile Image URL</label>
                                    <input type="text" placeholder="Profile Image URL" value={formData.profileImg || ''} onChange={e => setFormData({ ...formData, profileImg: e.target.value })} />
                                </div>
                            )}

                            {activeTab === 'worked_with' && (
                                <>
                                    <input type="text" placeholder="Company Name" value={formData.Name || ''} onChange={e => setFormData({ ...formData, Name: e.target.value })} required />
                                    <input type="text" placeholder="Logo Image URL" value={formData.logo || ''} onChange={e => setFormData({ ...formData, logo: e.target.value })} required />
                                </>
                            )}

                            {activeTab === 'socialMedia' && (
                                <>
                                    <input type="text" placeholder="LinkedIn URL" value={formData.linkedin || ''} onChange={e => setFormData({ ...formData, linkedin: e.target.value })} />
                                    <input type="text" placeholder="Github URL" value={formData.github || ''} onChange={e => setFormData({ ...formData, github: e.target.value })} />
                                    <input type="text" placeholder="Twitter URL" value={formData.twitter || ''} onChange={e => setFormData({ ...formData, twitter: e.target.value })} />
                                    <input type="text" placeholder="Instagram URL" value={formData.instagram || ''} onChange={e => setFormData({ ...formData, instagram: e.target.value })} />
                                </>
                            )}

                            <div className="btn-group">
                                <button type="submit" className="prime-btn">{isEditing ? 'Pulse/Update' : 'Commit/Create'}</button>
                                {isEditing && <button type="button" onClick={() => { setIsEditing(false); setFormData({}); }} className="alt-btn">Abort</button>}
                            </div>
                        </form>
                    </section>

                    <section className="admin-section list-card">
                        <div className="card-header">
                            <h3>Live Data Stream</h3>
                        </div>
                        <div className="data-list">
                            {['projects', 'products', 'worked_with'].includes(activeTab) ? (
                                items.map(item => (
                                    <div key={item.id} className="data-item">
                                        <div className="item-meta">
                                            <span className="item-title">{item.title || item.Name}</span>
                                            <span className="item-subtitle">{item.isPrivate ? '🔒 Private' : '🌐 Public'}</span>
                                        </div>
                                        <div className="item-ops">
                                            <button onClick={() => startEdit(item)} className="op-edit">Edit</button>
                                            <button onClick={() => handleDelete(item.id)} className="op-del">Del</button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="static-notice">Single object mode: Changes will update the main profile data immediately.</div>
                            )}
                        </div>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;
